import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    ListItem,
    Divider,
    TextField,
    Button,
    Checkbox,
    useTheme,
    useMediaQuery,
    Stack,
    IconButton,
    FormControlLabel,
    Container
} from '@mui/material';
import { PhotoCamera, Cancel, ExpandMore, ExpandLess } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../lib/axios';
import { useLocation } from 'react-router-dom';

const sanitizeImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http:/')) {
        return url.replace('http:/', 'http://');
    } else if (url.startsWith('https:/')) {
        return url.replace('https:/', 'https://');
    }
    return url;
};

const CheckInArtpage = () => {
    const location = useLocation();
    const bookingData = location.state?.bookingsData[0];
    const theme = useTheme();
    const fileInputRefs = useRef({});
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [loading, setLoading] = useState(false);
    const [articleData, setArticleData] = useState([]);
    const [error, setError] = useState(null);
    const [formState, setFormState] = useState({});
    const [parentData, setParentData] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});
    const [isConfirmed, setIsConfirmed] = useState(false);

    console.log('bookingData:', bookingData);
    
    useEffect(() => {
        if (!bookingData) return;

        const fetchChkArticles = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await axiosInstance.post('/TrnChkArticle/RetriveTrnChkArt', {
                    BookId: bookingData.BookId,
                    RoomTypeId: bookingData.RoomTypeId,
                    RoomBedTypeId: bookingData.RoomBedTypeId,
                    Flag: ''
                });

                const result = response.data;

                if (result.Status === 0) {
                    const articles = result.Data.TrnChkArtList[0]?.TrnChkArtList || [];
                    setArticleData(articles);
                    setParentData(result.Data.TrnChkArtList[0]);

                    const initialFormState = {};
                    const initialExpandedState = {};
                    articles.forEach((item) => {
                        initialFormState[item.ArtConfigDtlId] = {
                            image: sanitizeImageUrl(item.ArtImg1) || '',
                            remark: item.ArtRemk1 || ''
                        };
                        initialExpandedState[item.ArtConfigDtlId] = false;
                    });
                    setFormState(initialFormState);
                    setExpandedItems(initialExpandedState);
                    setIsConfirmed(result.Data.TrnChkArtList[0]?.ChkAcept === 'Y');
                } else {
                    const errorMessage = result.Message || 'Failed to fetch data';
                    setError(errorMessage);
                    toast.error(errorMessage);
                }
            } catch (err) {
                const errorMessage = 'Error fetching data';
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchChkArticles();
    }, [bookingData]);

    const handleImageChange = (id, file) => {
        setFormState((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                image: file
            }
        }));
    };

    const handleRemarkChange = (id, value) => {
        setFormState((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                remark: value
            }
        }));
    };

    const handleRemoveImage = (id) => {
        if (fileInputRefs.current[id]) {
            fileInputRefs.current[id].value = '';
        }
        setFormState((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                image: ''
            }
        }));
    };

    const handleToggleExpand = (id) => {
        setExpandedItems((prev) => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            if (articleData.length === 0 || !parentData) {
                const errorMessage = 'No article data available for submission.';
                setError(errorMessage);
                toast.error(errorMessage);
                setLoading(false);
                return;
            }
            const payload = [{
                ChkArtId: parentData.ChkArtId,
                PropId: parentData.PropId,
                BookId: parentData.BookId,
                ResidentId: parentData.ResidentId,
                ArtConfigId: parentData.ArtConfigId,
                CheckingDt: parentData.CheckingDt,
                ChkAcept: isConfirmed ? 'Y' : 'N',
                ChkAceptDt: parentData.ChkAceptDt,
                CheckedOutDt: parentData.CheckedOutDt,
                Remark: parentData.Remark,
                Status: parentData.Status,
                DBFLAG: parentData.ChkArtId ? 'U' : 'I',
                ManageTrnChkArtDtl: await Promise.all(
                    articleData.map(async (item) => ({
                        DBFLAG: item.ChkArtDtlId ? 'U' : 'I',
                        ChkArtDtlId: item.ChkArtDtlId || undefined,
                        ChkArtId: item.ChkArtId,
                        ArtConfigDtlId: item.ArtConfigDtlId,
                        StdRate: item.StdRate,
                        DmzChrg: item.DmzChrg,
                        Remark: item.Remark,
                        Status: item.Status,
                        ArtImg1: formState[item.ArtConfigDtlId]?.image instanceof File
                            ? await convertToBase64(formState[item.ArtConfigDtlId].image)
                            : '',
                        ImgName: formState[item.ArtConfigDtlId]?.image instanceof File
                            ? formState[item.ArtConfigDtlId].image.name
                            : '',
                        ArtRemk1: formState[item.ArtConfigDtlId]?.remark || ''
                    }))
                )
            }];

            const response = await axiosInstance.post('/TrnChkArticle/ManageTrnChkArt', payload);

            if (response.data.Status === 0) {
                toast.success(response.data.Message || 'Articles submitted successfully!');
            } else {
                const errorMessage = response.data.Message || 'Failed to submit data';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (err) {
            const errorMessage = 'Error submitting data: ' + err.message;
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const groupedByCategory = articleData.reduce((acc, article) => {
        const category = article.ChkArtCatName || 'Uncategorized';
        if (!acc[category]) acc[category] = [];
        acc[category].push(article);
        return acc;
    }, {});

    return (
        <Box sx={{ 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default'
        }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={theme.palette.mode}
            />
            <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                py: { xs: 1, sm: 2, md: 3 }
            }}>
                <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                    <Box sx={{ 
                        bgcolor: 'background.paper', 
                        borderRadius: 2, 
                        boxShadow: 3,
                        overflow: 'hidden',
                        mb: 2
                    }}>
                        <Box sx={{ 
                            p: { xs: 1.5, sm: 2, md: 2.5 }, 
                            borderBottom: '1px solid', 
                            borderColor: 'divider',
                            bgcolor: 'background.paper'
                        }}>
                            <Typography variant="h5" sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                            }}>
                                Check-In Articles
                            </Typography>
                        </Box>

                        <Box sx={{ 
                            p: { xs: 1.5, sm: 2, md: 2.5 },
                            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 220px)' },
                            overflow: 'auto'
                        }}>
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            
                            {error && (
                                <Typography color="error" sx={{ textAlign: 'center', py: 2 }}>
                                    {error}
                                </Typography>
                            )}

                            {!loading && !error && Object.keys(groupedByCategory).length === 0 && (
                                <Typography sx={{ textAlign: 'center', py: 4 }}>
                                    No articles found.
                                </Typography>
                            )}

                            {!loading && !error &&
                                Object.entries(groupedByCategory).map(([category, items]) => (
                                    <Box key={category} sx={{ mb: 3 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ 
                                                fontWeight: 600, 
                                                mb: 2,
                                                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                                color: 'primary.main',
                                                px: { xs: 1, sm: 0 }
                                            }}
                                        >
                                            {category}
                                        </Typography>

                                        {items.map((item, index) => (
                                            <React.Fragment key={item.ArtConfigDtlId}>
                                                <ListItem
                                                    disableGutters
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: isMobile ? 'column' : 'row',
                                                        alignItems: isMobile ? 'stretch' : 'center',
                                                        gap: { xs: 1.5, sm: 2, md: 3 },
                                                        px: { xs: 1, sm: 2 },
                                                        py: { xs: 1.5, sm: 2 },
                                                        bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
                                                        borderRadius: 1,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            bgcolor: 'action.selected',
                                                        }
                                                    }}
                                                >
                            
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            width: { xs: '100%', sm: 220, md: 250 },
                                                            minWidth: { sm: 220, md: 250 },
                                                            justifyContent: 'space-between'
                                                        }}
                                                    >
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center',
                                                            flex: 1
                                                        }}>
                                                            <Checkbox 
                                                                checked 
                                                                disabled 
                                                                size={isMobile ? "small" : "medium"} 
                                                                sx={{ mr: 1 }}
                                                            />
                                                            <Typography 
                                                                variant="body1" 
                                                                sx={{ 
                                                                    fontWeight: 500,
                                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                                }}
                                                            >
                                                                {item.ChkArticleName}
                                                            </Typography>
                                                        </Box>
                                                        {isMobile && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleExpand(item.ArtConfigDtlId)}
                                                                sx={{ 
                                                                    p: 1,
                                                                    ml: 1
                                                                }}
                                                            >
                                                                {expandedItems[item.ArtConfigDtlId] ? (
                                                                    <ExpandLess fontSize="small" />
                                                                ) : (
                                                                    <ExpandMore fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        )}
                                                    </Box>

                                  
                                                    {(!isMobile || expandedItems[item.ArtConfigDtlId]) && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                                gap: { xs: 1.5, sm: 2 },
                                                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                                flex: 1,
                                                            }}
                                                        >
                                                   
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 1,
                                                                flexShrink: 0
                                                            }}>
                                                                <IconButton
                                                                    color="primary"
                                                                    component="label"
                                                                    size={isMobile ? "medium" : "large"}
                                                                    sx={{
                                                                        border: `1px solid ${theme.palette.primary.main}`,
                                                                        borderRadius: 2
                                                                    }}
                                                                >
                                                                    <PhotoCamera fontSize={isMobile ? "small" : "medium"} />
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        hidden
                                                                        ref={(ref) => {
                                                                            if (ref) fileInputRefs.current[item.ArtConfigDtlId] = ref;
                                                                        }}
                                                                        onChange={(e) =>
                                                                            handleImageChange(item.ArtConfigDtlId, e.target.files[0])
                                                                        }
                                                                    />
                                                                </IconButton>

                                                                {formState[item.ArtConfigDtlId]?.image && (
                                                                    <Box sx={{ 
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        gap: 1 
                                                                    }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: { xs: 35, sm: 45, md: 50 },
                                                                                height: { xs: 35, sm: 45, md: 50 },
                                                                                borderRadius: 2,
                                                                                overflow: 'hidden',
                                                                                flexShrink: 0,
                                                                                backgroundColor: '#f0f0f0',
                                                                                border: `1px solid ${theme.palette.divider}`
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={
                                                                                    formState[item.ArtConfigDtlId].image instanceof File
                                                                                        ? URL.createObjectURL(formState[item.ArtConfigDtlId].image)
                                                                                        : sanitizeImageUrl(formState[item.ArtConfigDtlId].image)
                                                                                }
                                                                                alt={item.ChkArticleName}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover',
                                                                                    display: 'block',
                                                                                }}
                                                                                onError={(e) => {
                                                                                    console.error(`Failed to load image for ${item.ChkArticleName}:`, formState[item.ArtConfigDtlId].image);
                                                                                    e.target.src = '/static/images/fallback.jpg';
                                                                                }}
                                                                            />
                                                                        </Box>

                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRemoveImage(item.ArtConfigDtlId)}
                                                                            sx={{
                                                                                backgroundColor: 'error.light',
                                                                                color: 'error.contrastText',
                                                                                borderRadius: '50%',
                                                                                p: 0.5,
                                                                                '&:hover': {
                                                                                    backgroundColor: 'error.main',
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Cancel fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                )}
                                                            </Box>

                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label="Remark"
                                                                multiline
                                                                minRows={1}
                                                                maxRows={2}
                                                                value={formState[item.ArtConfigDtlId]?.remark || ''}
                                                                onChange={(e) =>
                                                                    handleRemarkChange(item.ArtConfigDtlId, e.target.value)
                                                                }
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: { xs: '100%', sm: 150 }
                                                                }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: 14, sm: 14 },
                                                                        py: { xs: 1, sm: 1 },
                                                                    },
                                                                }}
                                                                InputLabelProps={{
                                                                    sx: {
                                                                        fontSize: { xs: 14, sm: 14 },
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </ListItem>
                                                {index < items.length - 1 && (
                                                    <Divider sx={{ mx: { xs: 1, sm: 2 } }} />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                      <Box sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'background.paper',
                borderTop: `1px solid ${theme.palette.divider}`,
                py: { xs: 2, sm: 2.5 },
                px: { xs: 2, sm: 3 },
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                zIndex: 10
            }}>
                <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} 
                           spacing={2} 
                           alignItems={{ xs: 'stretch', sm: 'center' }}
                           justifyContent="space-between">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                    size={isMobile ? "small" : "medium"}
                                />
                            }
                            label={
                                <Typography variant="body1" sx={{ 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    fontWeight: 500
                                }}>
                                    I accept all terms and conditions
                                </Typography>
                            }
                            sx={{ m: 0 }}
                        />
                        
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={!isConfirmed || loading}
                            size={isMobile ? "medium" : "large"}
                            sx={{
                                minWidth: { xs: '100%', sm: 120, md: 140 },
                                py: { xs: 1, sm: 1.25 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 600
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Submit'
                            )}
                        </Button>
                    </Stack>
                </Container>
            </Box>
                </Container>
            </Box>

          
            <Box sx={{ 
                py: 2, 
                bgcolor: 'background.default',
                borderTop: `1px solid ${theme.palette.divider}`,
                mt: 'auto'
            }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ textAlign: 'center' }}
                    >
                        For any account-related issues, please contact our support team
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default CheckInArtpage;